import { useState, useEffect } from "react";
import { useAuth, api } from "../App";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Calendar as CalendarComponent } from "../components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import {
  Calendar as CalendarIcon,
  Plus,
  ChevronLeft,
  Clock,
  Users,
  Video,
  MapPin,
  Check,
  X,
} from "lucide-react";

export default function CalendarPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showAddModal, setShowAddModal] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const fetchData = async () => {
    try {
      const [interviewsRes, candidatesRes] = await Promise.all([
        api.get("/interviews"),
        api.get("/candidates"),
      ]);
      setInterviews(interviewsRes.data);
      setCandidates(candidatesRes.data.filter(c => c.status !== "rejected" && c.status !== "hired"));

      if (user?.role === "admin" || user?.role === "hr") {
        const usersRes = await api.get("/users");
        setUsers(usersRes.data.filter(u => u.role === "manager" || u.role === "admin"));
      }
    } catch (error) {
      toast.error("Failed to load calendar data");
    } finally {
      setLoading(false);
    }
  };

  const getInterviewsForDate = (date) => {
    return interviews.filter(interview => {
      const interviewDate = new Date(interview.scheduled_at);
      return (
        interviewDate.getDate() === date.getDate() &&
        interviewDate.getMonth() === date.getMonth() &&
        interviewDate.getFullYear() === date.getFullYear()
      );
    });
  };

  const todayInterviews = getInterviewsForDate(selectedDate);

  const getStatusColor = (status) => {
    const colors = {
      scheduled: "bg-info/20 text-info border-info/50",
      completed: "bg-success/20 text-success border-success/50",
      cancelled: "bg-error/20 text-error border-error/50",
      no_show: "bg-warning/20 text-warning border-warning/50",
    };
    return colors[status] || "bg-surface text-text-muted";
  };

  const handleStatusUpdate = async (interviewId, status) => {
    try {
      await api.put(`/interviews/${interviewId}/status?status=${status}`);
      toast.success(`Interview marked as ${status}`);
      fetchData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const canSchedule = user?.role === "admin" || user?.role === "hr";

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-primary text-2xl font-heading">LOADING...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex">
      
      
      <main className="flex-1 lg:ml-64 pb-20 lg:pb-0">
        {/* Header */}
        <header className="sticky top-0 z-40 glass-effect border-b border-border">
          <div className="flex items-center justify-between p-4 lg:p-6">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/me")}
                className="text-text-secondary hover:text-text-primary lg:hidden"
              >
                <ChevronLeft className="w-6 h-6" />
              </Button>
              <div>
                <h1 className="font-heading text-2xl text-text-primary flex items-center gap-2">
                  <CalendarIcon className="w-6 h-6 text-info" />
                  INTERVIEW CALENDAR
                </h1>
                <p className="text-text-muted text-sm">
                  {interviews.filter(i => i.status === "scheduled").length} upcoming interviews
                </p>
              </div>
            </div>
            {canSchedule && (
              <ScheduleInterviewModal
                open={showAddModal}
                onOpenChange={setShowAddModal}
                candidates={candidates}
                users={users}
                onSuccess={fetchData}
              />
            )}
          </div>
        </header>

        <div className="p-4 lg:p-6">
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Calendar */}
            <div className="lg:col-span-1">
              <div className="bg-surface border border-border rounded-lg p-4">
                <CalendarComponent
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => date && setSelectedDate(date)}
                  className="rounded-md"
                  modifiers={{
                    hasInterview: interviews.map(i => new Date(i.scheduled_at)),
                  }}
                  modifiersStyles={{
                    hasInterview: {
                      backgroundColor: "rgba(227, 25, 55, 0.2)",
                      borderRadius: "50%",
                    },
                  }}
                />
              </div>

              {/* Quick Stats */}
              <div className="mt-4 grid grid-cols-2 gap-4">
                <div className="bg-surface border border-border rounded-lg p-4 text-center">
                  <p className="stat-number text-info">
                    {interviews.filter(i => i.status === "scheduled").length}
                  </p>
                  <p className="text-text-muted text-xs">Scheduled</p>
                </div>
                <div className="bg-surface border border-border rounded-lg p-4 text-center">
                  <p className="stat-number text-success">
                    {interviews.filter(i => i.status === "completed").length}
                  </p>
                  <p className="text-text-muted text-xs">Completed</p>
                </div>
              </div>
            </div>

            {/* Day View */}
            <div className="lg:col-span-2">
              <div className="bg-surface border border-border rounded-lg">
                <div className="p-4 border-b border-border">
                  <h2 className="font-heading text-xl text-text-primary">
                    {selectedDate.toLocaleDateString("en-US", {
                      weekday: "long",
                      month: "long",
                      day: "numeric",
                    })}
                  </h2>
                  <p className="text-text-muted text-sm">
                    {todayInterviews.length} interview{todayInterviews.length !== 1 ? "s" : ""}
                  </p>
                </div>

                {todayInterviews.length > 0 ? (
                  <div className="divide-y divide-border">
                    {todayInterviews
                      .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at))
                      .map((interview, index) => (
                        <motion.div
                          key={interview.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.05 }}
                          className="p-4"
                          data-testid={`interview-card-${interview.id}`}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(interview.status)}`}>
                                  {interview.status.toUpperCase()}
                                </span>
                                <span className="px-3 py-1 rounded-full text-xs bg-primary/20 text-primary">
                                  {interview.interview_type.toUpperCase()}
                                </span>
                              </div>
                              <h3 className="font-heading text-lg text-text-primary">
                                {interview.candidate_name}
                              </h3>
                              <div className="flex flex-wrap gap-4 mt-2 text-sm text-text-muted">
                                <span className="flex items-center gap-1">
                                  <Clock className="w-4 h-4" />
                                  {new Date(interview.scheduled_at).toLocaleTimeString("en-US", {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })}
                                  <span className="text-text-muted">
                                    ({interview.duration_minutes} min)
                                  </span>
                                </span>
                                <span className="flex items-center gap-1">
                                  <Users className="w-4 h-4" />
                                  {interview.interviewer_names?.join(", ")}
                                </span>
                              </div>
                              {interview.notes && (
                                <p className="text-text-secondary text-sm mt-2">
                                  {interview.notes}
                                </p>
                              )}
                            </div>

                            {/* Actions */}
                            {interview.status === "scheduled" && (
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleStatusUpdate(interview.id, "completed")}
                                  className="border-success text-success hover:bg-success/10"
                                >
                                  <Check className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleStatusUpdate(interview.id, "cancelled")}
                                  className="border-error text-error hover:bg-error/10"
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            )}
                          </div>
                        </motion.div>
                      ))}
                  </div>
                ) : (
                  <div className="p-12 text-center">
                    <CalendarIcon className="w-12 h-12 mx-auto text-text-muted mb-4" />
                    <p className="text-text-secondary">
                      No interviews scheduled for this day
                    </p>
                    {canSchedule && (
                      <Button
                        onClick={() => setShowAddModal(true)}
                        className="mt-4 bg-primary hover:bg-primary-hover text-white font-heading"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        SCHEDULE INTERVIEW
                      </Button>
                    )}
                  </div>
                )}
              </div>

              {/* Upcoming Interviews */}
              <div className="mt-6 bg-surface border border-border rounded-lg">
                <div className="p-4 border-b border-border">
                  <h2 className="font-heading text-xl text-text-primary">UPCOMING THIS WEEK</h2>
                </div>
                <div className="p-4">
                  {interviews
                    .filter(i => {
                      const date = new Date(i.scheduled_at);
                      const now = new Date();
                      const weekFromNow = new Date();
                      weekFromNow.setDate(weekFromNow.getDate() + 7);
                      return i.status === "scheduled" && date >= now && date <= weekFromNow;
                    })
                    .slice(0, 5)
                    .map((interview) => (
                      <div
                        key={interview.id}
                        className="flex items-center gap-4 py-3 border-b border-border last:border-0"
                      >
                        <div className="w-12 h-12 rounded-lg bg-primary/20 flex flex-col items-center justify-center">
                          <span className="text-xs text-text-muted">
                            {new Date(interview.scheduled_at).toLocaleDateString("en-US", { weekday: "short" })}
                          </span>
                          <span className="font-heading text-primary">
                            {new Date(interview.scheduled_at).getDate()}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-text-primary font-medium truncate">
                            {interview.candidate_name}
                          </p>
                          <p className="text-text-muted text-sm">
                            {new Date(interview.scheduled_at).toLocaleTimeString("en-US", {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                            {" • "}
                            {interview.interview_type}
                          </p>
                        </div>
                      </div>
                    ))}
                  {interviews.filter(i => i.status === "scheduled").length === 0 && (
                    <p className="text-text-muted text-center py-4">
                      No upcoming interviews this week
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      
    </div>
  );
}

function ScheduleInterviewModal({ open, onOpenChange, candidates, users, onSuccess }) {
  const [formData, setFormData] = useState({
    candidate_id: "",
    interviewer_ids: [],
    scheduled_at: "",
    duration_minutes: 60,
    interview_type: "technical",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.candidate_id || formData.interviewer_ids.length === 0 || !formData.scheduled_at) {
      toast.error("Please fill all required fields");
      return;
    }

    setSubmitting(true);
    try {
      await api.post("/interviews", formData);
      toast.success("Interview scheduled! 📅");
      onOpenChange(false);
      onSuccess();
      setFormData({
        candidate_id: "",
        interviewer_ids: [],
        scheduled_at: "",
        duration_minutes: 60,
        interview_type: "technical",
        notes: "",
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to schedule interview");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button className="bg-primary hover:bg-primary-hover text-white font-heading neon-glow">
          <Plus className="w-4 h-4 mr-2" />
          SCHEDULE
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-surface border-border max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-text-primary">
            SCHEDULE INTERVIEW
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label className="text-text-secondary">Candidate *</Label>
            <select
              value={formData.candidate_id}
              onChange={(e) => setFormData({ ...formData, candidate_id: e.target.value })}
              className="w-full h-10 px-3 rounded-md bg-background border border-border text-text-primary focus:border-primary focus:outline-none"
              required
            >
              <option value="">Select candidate...</option>
              {candidates.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} - {c.job_title || "No title"}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <Label className="text-text-secondary">Interviewers *</Label>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {users.map((u) => (
                <label key={u.id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-background cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.interviewer_ids.includes(u.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFormData({ ...formData, interviewer_ids: [...formData.interviewer_ids, u.id] });
                      } else {
                        setFormData({ ...formData, interviewer_ids: formData.interviewer_ids.filter(id => id !== u.id) });
                      }
                    }}
                    className="w-4 h-4 rounded border-border bg-background text-primary focus:ring-primary"
                  />
                  <span className="text-text-primary">{u.name}</span>
                  <span className="text-text-muted text-xs">({u.role})</span>
                </label>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-text-secondary">Date & Time *</Label>
              <Input
                type="datetime-local"
                value={formData.scheduled_at}
                onChange={(e) => setFormData({ ...formData, scheduled_at: e.target.value })}
                className="bg-background border-border text-text-primary"
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="text-text-secondary">Duration (min)</Label>
              <select
                value={formData.duration_minutes}
                onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) })}
                className="w-full h-10 px-3 rounded-md bg-background border border-border text-text-primary focus:border-primary focus:outline-none"
              >
                <option value={30}>30 minutes</option>
                <option value={45}>45 minutes</option>
                <option value={60}>60 minutes</option>
                <option value={90}>90 minutes</option>
                <option value={120}>2 hours</option>
              </select>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-text-secondary">Interview Type</Label>
            <select
              value={formData.interview_type}
              onChange={(e) => setFormData({ ...formData, interview_type: e.target.value })}
              className="w-full h-10 px-3 rounded-md bg-background border border-border text-text-primary focus:border-primary focus:outline-none"
            >
              <option value="technical">Technical</option>
              <option value="hr">HR Round</option>
              <option value="cultural">Cultural Fit</option>
              <option value="final">Final Round</option>
            </select>
          </div>

          <div className="space-y-2">
            <Label className="text-text-secondary">Notes</Label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full h-20 px-3 py-2 rounded-md bg-background border border-border text-text-primary focus:border-primary focus:outline-none resize-none"
              placeholder="Any specific focus areas..."
            />
          </div>

          <div className="flex gap-4 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1 border-border text-text-primary"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="flex-1 bg-primary hover:bg-primary-hover text-white font-heading"
            >
              {submitting ? "Scheduling..." : "SCHEDULE"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
